import Table, { TableColDef } from 'components/table/Table';
import { Popover, Tooltip } from 'flowbite-react';
import moment, { Moment } from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { BookingFailureLogParams } from 'src/api/bookingFailureLogsApi';
import SearchInput from 'src/components/input/SearchInput';
import { DATE_FORMAT, defaultPageCount, routePaths } from 'src/utils/constants';
import { queryFilter } from 'src/utils/proptypes';
import {
   BookingFailureLog,
   BookingFailureLogResponse,
   BookingFailureLogsStatistics,
   BookingFailureLogsStatisticsResponse,
   BookingFailureLogErrorType,
   BookingFailureLogStatusType,
   BookingFailureLogErrors,
   BookingFailureLogStatus,
} from 'src/types/bookingFailureLog.type';
import Avatar from 'src/components/avatar/Avatar';
import SelectField, { SelectOption } from 'src/components/admin/form/selectField';
import TruncateTooltip from 'src/components/TruncateTooltip';
import { toSentenceCase } from 'src/utils/string.util';
import {
   HiOutlineAdjustments,
   HiOutlineCalendar,
   HiOutlineChevronLeft,
   HiOutlineChevronRight,
   HiOutlineClock,
   HiOutlineCreditCard,
   HiOutlineDuplicate,
   HiOutlineQuestionMarkCircle,
   HiOutlineServer,
   HiOutlineShieldExclamation,
   HiOutlineWifi,
} from 'react-icons/hi';
import { HiOutlineExclamationTriangle } from 'react-icons/hi2';
import { useGetBookingFailureLogs } from 'src/hooks/useBookingFailureLogs';
import { numberFormat } from 'src/utils/utils';
import Loading from 'src/components/loading';
import ScorecardModal from './ScorecardModal';
import FailureBreakDownBarChart from './FailureBreakDownBarChart';
import { useNavigate } from 'react-router-dom';
import { showToast } from 'src/components/toast';
import FilterModal from './FilterModal';
import InputField from 'src/components/admin/form/inputField';
import Calendar from 'src/components/HomeFilter/Calendar';
import { useTranslation } from 'react-i18next';

const errorOptions: SelectOption[] = BookingFailureLogErrors.map(key => {
   return { label: toSentenceCase(key), value: key };
});

const statusOptions: SelectOption[] = BookingFailureLogStatus.map(key => {
   return { label: toSentenceCase(key), value: key };
});

const errorConfig: Record<BookingFailureLogErrorType, { icon: React.ElementType; bg: string; text: string }> = {
   'Payment Gateway Error': {
      icon: HiOutlineCreditCard,
      bg: 'bg-purple-100',
      text: 'text-purple-700',
   },
   'Server Error': {
      icon: HiOutlineServer,
      bg: 'bg-red-100',
      text: 'text-red-700',
   },
   'Rate Limit': {
      icon: HiOutlineExclamationTriangle,
      bg: 'bg-purple-100',
      text: 'text-purple-700',
   },
   'Payment Timeout': {
      icon: HiOutlineClock,
      bg: 'bg-orange-100',
      text: 'text-orange-700',
   },
   Unknown: {
      icon: HiOutlineQuestionMarkCircle,
      bg: 'bg-gray-100',
      text: 'text-gray-700',
   },
   'Network Error': {
      icon: HiOutlineWifi,
      bg: 'bg-blue-100',
      text: 'text-blue-700',
   },
   'Validation Error': {
      icon: HiOutlineShieldExclamation,
      bg: 'bg-yellow-100',
      text: 'text-yellow-700',
   },
   'Session Expired': {
      icon: HiOutlineClock,
      bg: 'bg-gray-100',
      text: 'text-gray-700',
   },
   'Payment Declined': {
      icon: HiOutlineCreditCard,
      bg: 'bg-red-200',
      text: 'text-red-800',
   },
};

const statusConfig: Record<BookingFailureLogStatusType, { bg: string; text: string }> = {
   RESOLVED: {
      bg: 'bg-green-700',
      text: 'text-green-700',
   },
   NEW: {
      bg: 'bg-red-700',
      text: 'text-red-700',
   },
   INVESTIGATING: {
      bg: 'bg-yellow-700',
      text: 'text-yellow-700',
   },
   IGNORED: {
      bg: 'bg-gray-700',
      text: 'text-gray-700',
   },
};

const BookingFailureTracker = () => {
   const [t] = useTranslation();
   
   const [selectedMonth, setSelectedMonth] = useState(moment());
   const [dateFilter, setDateFilter] = useState<moment.MomentInput[]>([]);
   const [openCalendar, setOpenCalendar] = useState(false);
   const [filters, setFilters] = useState<queryFilter & BookingFailureLogParams>({
      status: '',
      errorType: '',
      page: 0,
      limit: defaultPageCount,
      isFetchAllSites: false,
   });

   const navigate = useNavigate();
   const [openScorecardModal, setOpenScorecardModal] = useState(false);
   const [openMobileFilterModal, setOpenMobileFilterModal] = useState(false);

   useEffect(() => {
      const handleResize = () => {
         if (window.innerWidth >= 768) setOpenMobileFilterModal(false);
      };
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
   }, []);

   // Fetch get booking failure logs
   const { data: getBookingFailureLogs, isLoading, isFetching } = useGetBookingFailureLogs(filters);

   const bookingFailureLogs: BookingFailureLog[] = useMemo(() => {
      const bookingFailureLogsResponse: BookingFailureLogResponse[] = getBookingFailureLogs?.data?.entities;

      return bookingFailureLogsResponse?.map(log => ({
         id: log?.id,
         siteName: log?.siteName || 'Nonprofit Trips',
         bookingId: log?.booking?.id,
         timestamp: log?.created,
         user: log?.user,
         errorType: log?.errorDetail?.type as BookingFailureLogErrorType,
         status: log?.status as BookingFailureLogStatusType,
         property: log?.organization?.name,
         errorMessage: log?.errorDetail?.message,
         amount: log?.booking?.paidAmount,
      }));
   }, [getBookingFailureLogs?.data?.entities]);

   const bookingFailureStats: BookingFailureLogsStatistics = useMemo(() => {
      const bookingFailureLogsResponse: BookingFailureLogsStatisticsResponse = getBookingFailureLogs?.data?.dashBoard;

      return {
         failureRate24h: bookingFailureLogsResponse?.failureRate24h?.rate,
         failureDiffRate24h: bookingFailureLogsResponse?.failureRate24h?.diffPercentage,
         failure24h: bookingFailureLogsResponse?.failures24h?.count,
         failureDiff24h: bookingFailureLogsResponse?.failures24h?.diffPercentage,
         breakdown7Days: bookingFailureLogsResponse?.breakdown7Days,
         topErrorType: bookingFailureLogsResponse?.topErrorType,
      };
   }, [getBookingFailureLogs?.data?.dashBoard]);

   // Search
   const handleSearch = (value: string) => {
      setFilters(prev => ({ ...prev, page: 0, searchQuery: value }));
   };

   // Sort
   const handleSort = (model: { field: keyof BookingFailureLog; order: 'ASC' | 'DESC' | 'NONE' }) => {
      if (model && model.order !== 'NONE') {
         setFilters(prev => ({
            ...prev,
            sortBy: model.field,
            sortByType: model.order,
         }));
      } else {
         setFilters(prev => {
            const { sortBy: _sortBy, sortByType: _sortByType, ...rest } = prev;
            return rest;
         });
      }
   };

   const handleViewDetail = (id: string) => {
      navigate(routePaths.ADMIN.BOOKING_FAILURE_TRACKER.DETAIL.replace(':id', id || ''));
   };

   const columns: TableColDef<BookingFailureLog>[] = useMemo(
      () => [
         {
            headerName: 'Booking ID',
            field: 'bookingId',
            sortable: true,
            minWidth: 180,
            maxWidth: 240,
            render: (row: BookingFailureLog) => {
               return (
                  <div className="flex justify-start items-center gap-1">
                     <Tooltip content={String(row.bookingId || row.id)} trigger="hover" style="dark">
                        <p className="self-center mb-0 text-black font-semibold text-sm line-clamp-2">{row.bookingId || row.id}</p>
                     </Tooltip>
                     <HiOutlineDuplicate
                        className="h-4 w-4 flex-shrink-0 text-twGray"
                        onClick={async e => {
                           e.stopPropagation();
                           await navigator.clipboard.writeText(row.bookingId || row.id);

                           showToast('success', 'Booking ID is copied to clipboard');
                        }}
                     />
                  </div>
               );
            },
         },
         {
            headerName: 'Timestamp',
            field: 'created',
            minWidth: 170,
            maxWidth: 170,
            render: (row: BookingFailureLog) => {
               const value = row.timestamp ? moment(row.timestamp).format('MMM D, YYYY, h:mm A') : 'Never';

               return (
                  <Tooltip content={value} trigger="hover" style="dark">
                     <div className="self-center text-black text-sm whitespace-break-spaces w-fit line-clamp-2">{value}</div>
                  </Tooltip>
               );
            },
         },
         {
            headerName: 'Traveler',
            field: 'traveler',
            minWidth: 320,
            render: (row: BookingFailureLog) => {
               return (
                  <div className="flex items-center">
                     <Avatar user={row.user} />
                     <div className="ml-2.5 min-w-0 flex-1">
                        <TruncateTooltip text={`${row.user?.firstName ?? ''} ${row.user?.lastName ?? ''}`.trim()}>
                           <p className="max-w-[220px] mb-0 text-black font-semibold text-sm truncate">{`${row.user?.firstName} ${row.user?.lastName}`}</p>
                        </TruncateTooltip>
                        <Tooltip content={String(row.user?.emailAddress ?? '')} trigger="hover" style="dark">
                           <p className="mb-0 text-xs text-twGray line-clamp-2 break-all">{`${row.user?.emailAddress}`}</p>
                        </Tooltip>
                     </div>
                  </div>
               );
            },
         },
         {
            headerName: 'Error Type',
            field: 'errorType',
            minWidth: 160,
            render: (row: BookingFailureLog) => {
               const { icon: Icon, bg, text } = errorConfig[row.errorType as BookingFailureLogErrorType] || {};

               return (
                  <div className="flex items-center">
                     <div className={`flex items-center gap-1.25 p-1.25 rounded ${bg}`}>
                        <Icon className={`h-4 w-4 flex-shrink-0 ${text}`} />
                        <Tooltip content={String(row.errorType ?? '')} trigger="hover" style="dark">
                           <span className={`text-sm ${text} line-clamp-1`}>{row.errorType}</span>
                        </Tooltip>
                     </div>
                  </div>
               );
            },
         },
         {
            headerName: 'Message Error',
            field: 'messageError',
            minWidth: 250,
            flex: 1,
            render: (row: BookingFailureLog) => {
               return (
                  <Tooltip content={String(row.errorMessage ?? '')} trigger="hover" style="dark">
                     <p className="self-center mb-0 text-black text-sm w-fit line-clamp-3">{row.errorMessage}</p>
                  </Tooltip>
               );
            },
         },
         {
            headerName: 'BookingFailureLogStatusType',
            field: 'status',
            minWidth: 120,
            flex: 1,
            render: (row: BookingFailureLog) => {
               const { bg, text } = statusConfig[row?.status as BookingFailureLogStatusType] || {};

               return (
                  <div className="flex items-center space-x-2 mb-2">
                     <span className={`${text} w-4 h-4 ${bg} rounded-full`}></span>
                     <span className={`${text} text-sm font-semibold`}>{row.status}</span>
                  </div>
               );
            },
         },
         {
            headerName: 'Site Name',
            field: 'siteName',
            minWidth: 120,
            render: (row: BookingFailureLog) => {
               return <p className="self-center mb-0 text-black text-sm w-fit line-clamp-2">{row.siteName}</p>;
            },
         },
         {
            headerName: 'Amount',
            field: 'amount',
            minWidth: 100,
            render: (row: BookingFailureLog) => {
               return <p className="self-center mb-0 text-black text-sm w-fit line-clamp-2">{row.amount ? numberFormat(Number(row.amount)) : '-'}</p>;
            },
         },
         {
            align: 'right',
            field: 'actions',
            minWidth: 140,
            maxWidth: 140,
            headerName: '',
            render: (row: BookingFailureLog) => {
               return (
                  <div
                     onClick={() => handleViewDetail(row.id)}
                     className="self-center flex items-center cursor-pointer border border-border rounded-6 px-2.5 py-1.5 text-black max-h-10 whitespace-nowrap"
                  >
                     View Detail
                  </div>
               );
            },
         },
      ],
      [filters.type],
   );

   return isLoading ? (
      <Loading />
   ) : (
      <div className="flex flex-col bg-white h-full gap-5">
         <h1 className="text-2xl font-bold">Booking Failure Tracker</h1>

         {/* 4 blocks in flex row, responsive */}
         <div className="hidden lg:flex flex-wrap gap-6">
            <div className="m-w-40 h-[165px] gap-1.25 p-3.75 rounded-10 border border-border bg-lightGray shadow-[1px_1px_2px_0px_#00000033]">
               <h2 className="text-xl font-bold">Failures (24h)</h2>
               <p className="text-[44px] font-bold text-red-600">{bookingFailureStats?.failure24h}</p>
               <span className="text-green-600 text-base font-semibold">
                  {bookingFailureStats?.failureDiff24h}% <strong className="text-black font-semibold">vs yesterday</strong>
               </span>
            </div>

            <div className="m-w-[185px] h-[165px] gap-1.25 p-3.75 rounded-10 border border-border bg-lightGray shadow-[1px_1px_2px_0px_#00000033]">
               <h2 className="text-xl font-bold">Failure Rate (24h)</h2>
               <p className="text-[44px] font-bold text-red-600">{bookingFailureStats?.failureRate24h}%</p>
               <span className="text-red-600 text-base font-semibold">{bookingFailureStats?.failureDiffRate24h}%</span>
            </div>

            <div className="m-w-[360px] h-[165px] gap-1.25 p-3.75 rounded-10 border border-border bg-lightGray shadow-[1px_1px_2px_0px_#00000033]">
               <h2 className="text-xl font-bold">Failure Breakdown (Last 7 Days)</h2>
               {bookingFailureStats?.breakdown7Days && bookingFailureStats?.breakdown7Days.length ? (
                  <FailureBreakDownBarChart breakdown7Days={bookingFailureStats.breakdown7Days} />
               ) : null}
            </div>

            <div className="m-w-[236px] h-[165px] gap-1.25 p-3.75 rounded-10 border border-border bg-lightGray shadow-[1px_1px_2px_0px_#00000033]">
               <h2 className="text-xl font-bold mb-3.75">Top Error Type</h2>
               <p className="text-xl font-bold text-red-600">{bookingFailureStats?.topErrorType?.type}</p>
               <p className="text-base font-bold text-black">{bookingFailureStats?.topErrorType?.occurrences} occurrences</p>
            </div>
         </div>

         <div
            className="flex lg:hidden flex-wrap gap-6 justify-center p-3.75 bg-lightGray rounded-6 font-bold text-base cursor-pointer opacity-80 hover:opacity-100"
            onClick={() => setOpenScorecardModal(true)}
         >
            Overview Scorecards
         </div>

         <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4 min-w-0 flex-1 flex-wrap">
               <div className="flex flex-row items-center gap-2 w-full md:w-auto md:max-w-[300px] md:min-w-40">
                  <div className="flex-1">
                     <SearchInput handleSearch={handleSearch} searchValue={filters?.searchQuery || ''} />
                  </div>
                  <div className="md:hidden shrink-0">
                     <Tooltip content={<p className="font-medium text-sm mb-0">Filter</p>}>
                        <div
                           className="w-8.5 aspect-square border-borderStrong border rounded-6 flex items-center justify-center cursor-pointer hover:scale-105"
                           onClick={() => setOpenMobileFilterModal(!openMobileFilterModal)}
                        >
                           <HiOutlineAdjustments className="w-4 h-4" />
                        </div>
                     </Tooltip>
                  </div>
               </div>

               <div className="hidden md:flex flex-1 min-w-[140px] max-w-[200px]">
                  <SelectField
                     clearable
                     options={errorOptions}
                     placeholder="Error Type"
                     searchable={false}
                     theme={{ border: 'border-border', input: 'min-h-8', container: 'w-full' }}
                     onChange={(value: SelectOption | SelectOption[]) => {
                        if (!value) {
                           setFilters(preValues => {
                              return {
                                 ...preValues,
                                 page: 0,
                                 errorType: undefined,
                              };
                           });
                        } else {
                           setFilters(preValues => {
                              return {
                                 ...preValues,
                                 page: 0,
                                 errorType: (value as SelectOption).value,
                              };
                           });
                        }
                     }}
                     value={filters?.errorType}
                     disabled={isFetching}
                  />
               </div>

               <div className="hidden md:flex flex-1 min-w-[140px] max-w-[200px]">
                  <SelectField
                     clearable
                     options={statusOptions}
                     placeholder="Status"
                     searchable={false}
                     theme={{ border: 'border-border', input: 'min-h-8', container: 'w-full' }}
                     onChange={(value: SelectOption | SelectOption[]) => {
                        if (!value) {
                           setFilters(preValues => {
                              return {
                                 ...preValues,
                                 page: 0,
                                 status: undefined,
                              };
                           });
                        } else {
                           setFilters(preValues => {
                              return {
                                 ...preValues,
                                 page: 0,
                                 status: (value as SelectOption).value,
                              };
                           });
                        }
                     }}
                     value={filters?.status}
                     disabled={isFetching}
                  />
               </div>

               {/* Date range picker */}
               <div className="hidden md:flex w-full md:w-auto md:min-w-[230px]">
                  <Popover
                     open={openCalendar}
                     onOpenChange={active => {
                        if (!active) {
                           setSelectedMonth(dateFilter.length !== 0 ? moment(dateFilter[0]) : moment());
                        }
               
                        setOpenCalendar(active);
                     }}
                     content={
                        <div>
                           <div className="flex justify-between">
                              <button
                                 className="active:bg-border hover:bg-border min-w-6 min-h-6 flex items-center justify-center rounded"
                                 onClick={e => {
                                    e.stopPropagation();
                                    e.preventDefault();
               
                                    setSelectedMonth(preValue => preValue.clone().subtract(1, 'month'));
                                 }}
                              >
                                 <HiOutlineChevronLeft size={18} color="black" />
                              </button>
                              <span className="text-black font-bold text-sm">{selectedMonth.format('MMMM YYYY')}</span>
                              <button
                                    className="active:bg-border hover:bg-border min-w-6 min-h-6 flex items-center justify-center rounded disabled:opacity-50 disabled:bg-border"
                                    disabled={selectedMonth.isAfter(moment().add(10, 'year'))}
                                    onClick={e => {
                                       e.stopPropagation();
                                       e.preventDefault();
               
                                       setSelectedMonth(preValue => preValue.clone().add(1, 'month'));
                                    }}
                              >
                                    <HiOutlineChevronRight size={18} color="black" />
                              </button>
                           </div>
                           <div className="mt-3">
                              <Calendar
                                 disabledBefore={moment().subtract(10, 'year')}
                                 range={dateFilter}
                                 selectedMonth={selectedMonth}
                                 onChange={(value?: Moment) => {
                                    if (!value) return;
               
                                    if (dateFilter.length % 2 === 0) {
                                       setDateFilter([value]);
                                       return;
                                    }
               
                                    const [firstDate] = dateFilter;
                                    const fromDate = moment.min([firstDate as Moment, value]);
                                    const toDate = moment.max([firstDate as Moment, value]);
               
                                    setDateFilter([fromDate, toDate]);
                                    setFilters(p => ({
                                       ...p,
                                       fromDate: fromDate.format(DATE_FORMAT.SERVER_DATE),
                                       toDate: toDate.format(DATE_FORMAT.SERVER_DATE),
                                    }));
                                    setOpenCalendar(false);
                                 }}
                                 isOnAdminSite
                              />
                           </div>
                        </div>
                     }
                     theme={{
                        base: 'w-[calc(100vw-30px)] !left-1.75 p-3.75 sm:max-w-[346px]',
                        content: 'z-10 overflow-hidden rounded-none',
                     }}
                     >
                        <InputField
                           acceptChars={/[0-9/]/}
                           clearable
                           placeholder={t('transaction.dateRange')}
                           endIcon={<HiOutlineCalendar size={16} color="gray" />}
                           onEndIconClick={() => setOpenCalendar(!openCalendar)}
                           theme={{ border: 'border-gray-200 max-h-8.5', input: 'min-h-8', endIcon: 'w-6.5 min-w-6.5 h-6.5 min-h-6.5' }}
                           value={
                              dateFilter?.length === 2
                                 ? `${moment(dateFilter?.[0]).format(DATE_FORMAT.STANDARD)} - ${moment(dateFilter?.[1]).format(DATE_FORMAT.STANDARD)}`
                                 : ''
                           }
                           onClear={() => {
                              setDateFilter([]);
                              setFilters(preValues => {
                              return {
                                       ...preValues,
                                       fromDate: undefined,
                                       toDate: undefined,
                                    };
                              });
                           }}
                        />
                  </Popover>
               </div>

               <div className="hidden md:flex h-8.5 bg-border rounded-6 p-0.5 gap-0.5 justify-center items-center">
                  <button
                     disabled={isFetching}
                     onClick={() =>
                        setFilters(preValues => {
                           return {
                              ...preValues,
                              page: 0,
                              isFetchAllSites: false,
                           };
                        })
                     }
                     className={`h-full flex-1 text-sm font-semibold rounded-md px-3.75 text-nowrap ${!filters.isFetchAllSites ? 'bg-primary text-white rounded-6 border' : ''}`}
                  >
                     This Site
                  </button>
                  <button
                     disabled={isFetching}
                     onClick={() =>
                        setFilters(preValues => {
                           return {
                              ...preValues,
                              page: 0,
                              isFetchAllSites: true,
                           };
                        })
                     }
                     className={`h-full flex-1 text-sm font-semibold rounded-md px-3.75 text-nowrap ${filters.isFetchAllSites ? 'bg-primary text-white rounded-6 border' : ''}`}
                  >
                     All Sites
                  </button>
               </div>
            </div>
         </div>

         <div className="flex flex-col flex-1 mt-3 justify-between h-full overflow-hidden">
            <Table
               theme={{
                  head: {
                     base: 'bg-white border-b-2 group/head',
                     cell: {
                        base: 'px-4 py-1.5 bg-white text-black text-xs normal-case text-left',
                     },
                  },
                  row: {
                     base: 'cursor-pointer py-1.5',
                     hovered: 'hover:bg-gray-50',
                     striped: '',
                  },
               }}
               isLoading={isFetching}
               columns={columns}
               rows={bookingFailureLogs}
               currentPage={filters?.page || 0}
               totalPage={Math.ceil(getBookingFailureLogs?.data?.totalEntities / defaultPageCount)}
               onSort={models => {
                  const model = models[0];
                  if (!model) return;

                  handleSort({
                     field: model.field as keyof BookingFailureLog,
                     order: model.order.toUpperCase() as 'ASC' | 'DESC' | 'NONE',
                  });
               }}
               onPageChanged={(page: number) => {
                  setFilters(preValue => ({
                     ...preValue,
                     page,
                  }));
               }}
               onRowSelect={row => handleViewDetail(row.id)}
            />

            {/* Modals */}
            <ScorecardModal
               openModal={openScorecardModal}
               setOpenModal={setOpenScorecardModal}
               bookingFailureStats={bookingFailureStats}
               breakdown7Days={bookingFailureStats?.breakdown7Days}
            />

            <FilterModal
               isFetching={isFetching}
               isOpen={openMobileFilterModal}
               setIsOpen={setOpenMobileFilterModal}
               errorOptions={errorOptions}
               statusOptions={statusOptions}
               filters={filters}
               setFilters={setFilters}
            />
         </div>
      </div>
   );
};

export default BookingFailureTracker;
